import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import PostPurchaseService from 'services/PostPurchaseService';
import { ConfirmOptions } from './ConfirmDialog';
import { StandardPagePreview, errorText, pacific, pct, usdCents } from './shared';
import { ExperimentReport, ExperimentStatus, Upsell, UpsellVersion, VersionLook } from './types';
import { VersionsChart } from './VersionsChart';

// The A/B tests tab. A test compares versions of one upsell that shipped (the
// collector records each, with how it looked: versions.js), chosen here with
// any split, and is kept in the dashboard's settings store. Every version's
// numbers are its design's, counted only while the test ran.

type Message = { severity: 'success' | 'error'; text: string };
type Confirm = (options: ConfirmOptions) => Promise<boolean>;
type Versions = UpsellVersion[] | null | undefined;

const STATUS_CHIP: Record<ExperimentStatus, { label: string; color: 'default' | 'success' | 'info' | 'warning' }> = {
  draft: { label: 'Draft', color: 'warning' },
  running: { label: 'Running', color: 'success' },
  stopped: { label: 'Stopped', color: 'default' },
};

const SOURCE_LABEL: Record<UpsellVersion['source'], string> = {
  current: 'Shipped',
  candidate: 'Candidate',
  backfill: 'Earlier version',
};

// Whole percents adding to 100, as even as they go (the first ones get the rest).
function evenSplit(ids: string[]): Record<string, number> {
  const base = Math.floor(100 / ids.length);
  return Object.fromEntries(ids.map((id, index) => [id, base + (index < 100 - base * ids.length ? 1 : 0)]));
}

const splitTotal = (split: Record<string, number>) => Object.values(split).reduce((sum, weight) => sum + weight, 0);

const lookAsPreview = (look: VersionLook) => ({
  display: look.display,
  imageUrl: look.imageUrl,
  imageDescription: look.imageDescription,
  chargeCents: look.chargeCents,
});

// One version's page as it looked when recorded, in miniature.
function VersionPage({ look, phone = true, zoom }: { look: VersionLook | null; phone?: boolean; zoom: number }) {
  if (!look) return <Alert severity="info">No snapshot of this version's page.</Alert>;
  return (
    <Box sx={{ width: phone ? 390 : 960, zoom }}>
      <StandardPagePreview preview={lookAsPreview(look)} phone={phone} />
    </Box>
  );
}

// Versions side by side, as they looked, as phones or as desktops.
function CompareVersions({ ids, versions, labels }: { ids: string[]; versions: Versions; labels: Record<string, string> }) {
  const [view, setView] = React.useState<'phone' | 'desktop'>('phone');
  if (versions === undefined) return <LinearProgress aria-label="loading the versions' pages" sx={{ my: 1.5 }} />;
  if (versions === null) return <Alert severity="error" sx={{ my: 1.5 }}>Could not load the versions' pages.</Alert>;
  return (
    <Box sx={{ my: 1.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }} aria-label="versions side by side">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="caption" color="text.secondary">Compare as</Typography>
        {(['phone', 'desktop'] as const).map(option => (
          <Button key={option} size="small" variant={view === option ? 'contained' : 'text'} onClick={() => setView(option)}>
            {option === 'phone' ? 'Phone' : 'Desktop'}
          </Button>
        ))}
        <Typography variant="caption" color="text.secondary">
          Each as it looked when recorded (prices as of then).
        </Typography>
      </Stack>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', overflowX: 'auto' }}>
        {ids.map(id => {
          const version = versions.find(entry => entry.designId === id);
          return (
            <Box key={id} sx={{ flex: 'none' }}>
              <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 0.5, maxWidth: view === 'phone' ? 300 : 480 }}>
                <strong>{labels[id] ?? version?.label ?? id}</strong> · design {id}
              </Typography>
              <VersionPage look={version?.look ?? null} phone={view === 'phone'} zoom={view === 'phone' ? 0.75 : 0.5} />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// The API's limit (experiment.js): a test compares two to six versions.
const MAX_VERSIONS = 6;

// A new test: pick an upsell, tick its versions (each shown as it looked, with
// its numbers so far), set the split, name it. Saved as a draft to start later.
function NewExperiment({ upsells, versionsOf, loadVersions, service, onCreated, onMessage }: {
  upsells: Upsell[];
  versionsOf: (upsellId: string) => Versions;
  loadVersions: (upsellId: string) => void;
  service: PostPurchaseService;
  onCreated: (upsellId: string, experimentId: string) => Promise<void>;
  onMessage: (message: Message) => void;
}) {
  const [upsellId, setUpsellId] = React.useState(upsells[0]?.id ?? '');
  const [picked, setPicked] = React.useState<string[]>([]);
  const [split, setSplit] = React.useState<Record<string, string>>({});
  const [label, setLabel] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    if (upsellId) loadVersions(upsellId);
  }, [upsellId, loadVersions]);
  const versions = versionsOf(upsellId);

  const choose = (ids: string[]) => {
    setPicked(ids);
    setSplit(Object.fromEntries(Object.entries(evenSplit(ids)).map(([id, weight]) => [id, String(weight)])));
  };
  const weights = Object.fromEntries(picked.map(id => [id, Number(split[id])]));
  const splitOk = picked.every(id => Number.isInteger(weights[id]) && weights[id] >= 0) && splitTotal(weights) === 100;
  const ready = picked.length >= 2 && picked.length <= MAX_VERSIONS && splitOk && label.trim().length > 0;

  async function create() {
    setSaving(true);
    try {
      const created = await service.createExperiment(upsellId, { label: label.trim(), versions: picked, weights });
      onMessage({ severity: 'success', text: `Draft "${created.label}" saved. Start it when ready.` });
      await onCreated(upsellId, created.id);
    } catch (error) {
      onMessage({ severity: 'error', text: `Could not save the test: ${errorText(error)}` });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }} aria-label="new experiment">
      <Typography variant="subtitle1" fontWeight={600}>New experiment</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Compare any versions of one upsell that have shipped: each is shown as it looked, with its numbers so far.
        New copy or prices still come in through code; once shipped, they appear here.
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
        <span id="new-experiment-upsell-label" hidden>Upsell</span>
        <Select
          size="small"
          labelId="new-experiment-upsell-label"
          value={upsellId}
          onChange={event => {
            setUpsellId(String(event.target.value));
            choose([]);
          }}
        >
          {upsells.map(upsell => (
            <MenuItem key={upsell.id} value={upsell.id}>{upsell.id}</MenuItem>
          ))}
        </Select>
        <TextField
          size="small"
          label="Name"
          value={label}
          onChange={event => setLabel(event.target.value)}
          inputProps={{ maxLength: 60, 'aria-label': 'experiment name' }}
          sx={{ minWidth: 260 }}
        />
      </Stack>
      {versions === undefined && <LinearProgress aria-label="loading versions" />}
      {versions === null && <Alert severity="error">Could not load this upsell's versions.</Alert>}
      {versions && !versions.length && <Alert severity="info">No versions recorded for this upsell yet.</Alert>}
      {versions && versions.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }} aria-label="versions">
          {versions.map(version => {
            const on = picked.includes(version.designId);
            return (
              <Paper
                key={version.designId}
                variant="outlined"
                sx={{ p: 1.5, flex: 'none', width: 260, borderColor: on ? 'primary.main' : 'divider', borderWidth: on ? 2 : 1, opacity: version.runnable ? 1 : 0.6 }}
              >
                <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                  <Checkbox
                    size="small"
                    checked={on}
                    disabled={!version.runnable || (!on && picked.length >= MAX_VERSIONS)}
                    onChange={() => choose(on ? picked.filter(id => id !== version.designId) : [...picked, version.designId])}
                    inputProps={{ 'aria-label': `include ${version.label}` }}
                    sx={{ p: 0.5 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>{version.label}</Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
                      {version.current && <Chip size="small" color="success" label="Live now" />}
                      <Chip size="small" label={SOURCE_LABEL[version.source]} />
                    </Stack>
                  </Box>
                </Stack>
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                  design {version.designId}
                  {version.firstSeen ? ` · ${pacific(version.firstSeen)}` : ' · not recorded yet'}
                </Typography>
                <Typography variant="caption" component="p">
                  {version.stats
                    ? `${version.stats.viewed} views · ${pct(version.stats.accepted, version.stats.viewed)} take rate · ${version.stats.accepted} yes`
                    : 'Not shown to customers yet'}
                </Typography>
                {!version.runnable && (
                  <Typography variant="caption" color="error" component="p">Can't run today: it no longer passes today's checks.</Typography>
                )}
                <Box sx={{ mt: 1, maxHeight: 420, overflow: 'hidden' }}>
                  <VersionPage look={version.look} zoom={0.6} />
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
      {picked.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Split</Typography>
          <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="center">
            {picked.map(id => (
              <TextField
                key={id}
                size="small"
                type="number"
                label={versions?.find(version => version.designId === id)?.label.slice(0, 32) ?? id}
                value={split[id] ?? ''}
                onChange={event => setSplit(current => ({ ...current, [id]: event.target.value }))}
                inputProps={{ min: 0, max: 100, 'aria-label': `percent of checkouts for ${id}` }}
                sx={{ width: 200, mb: 1 }}
              />
            ))}
            {!splitOk && <Typography variant="caption" color="error">Whole percents adding up to 100.</Typography>}
          </Stack>
        </Box>
      )}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
        <Button variant="contained" disabled={!ready || saving} onClick={create}>Save draft</Button>
        <Typography variant="caption" color="text.secondary">
          {picked.length < 2 ? 'Pick at least two versions (up to six).' : 'Saved as a draft: nothing changes for customers until you start it.'}
        </Typography>
      </Stack>
    </Paper>
  );
}

// One test: each version's views, acceptances and profit per view (what it is
// judged on, before payment fees and shipping), how likely each is the most
// profitable, their pages side by side, and the controls. The result is only
// called at the planned sample and 90%: stopping on an early lead picks false winners.
function ExperimentPanel({ report, versions, service, confirm, onChanged, onMessage }: {
  report: ExperimentReport;
  versions: Versions;
  service: PostPurchaseService;
  confirm: Confirm;
  onChanged: () => Promise<void>;
  onMessage: (message: Message) => void;
}) {
  const [saving, setSaving] = React.useState(false);
  const [shown, setShown] = React.useState<string[]>([]);
  const [split, setSplit] = React.useState<Record<string, string>>({});
  const arms = report.arms ?? report.versions.map(designId => ({ designId, label: designId, weight: report.weights?.[designId] ?? 0 }) as NonNullable<ExperimentReport['arms']>[number]);
  const labels = Object.fromEntries(arms.map(arm => [arm.designId, arm.label]));
  const draft = report.status === 'draft';
  const running = report.status === 'running';
  const weights = Object.fromEntries(arms.map(arm => [arm.designId, Number(split[arm.designId] ?? report.weights?.[arm.designId] ?? 0)]));
  const splitOk = arms.every(arm => Number.isInteger(weights[arm.designId]) && weights[arm.designId] >= 0) && splitTotal(weights) === 100;
  const planned = report.plannedViewsPerArm ?? 0;
  const minViews = report.minViewsToRead ?? 0;
  const enough = arms.every(arm => (arm.viewed ?? 0) >= planned);
  const leader = arms.reduce((best, arm) => ((arm.chanceBest ?? 0) > (best.chanceBest ?? 0) ? arm : best), arms[0]);
  const forced = report.forceVersion ? arms.find(arm => arm.designId === report.forceVersion) : null;

  async function change(body: { status?: 'running' | 'stopped'; weights?: Record<string, number>; forceVersion?: string | null }, done: string) {
    setSaving(true);
    try {
      await service.setExperiment(report.upsellId, report.id, body);
      onMessage({ severity: 'success', text: `${done} Live checkouts follow within 30 seconds.` });
      await onChanged();
    } catch (error) {
      onMessage({ severity: 'error', text: `Could not update "${report.label}": ${errorText(error)} Nothing changed.` });
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!(await confirm({ title: `Delete the draft "${report.label}"?`, body: 'It was never started, so no results are lost.', confirmLabel: 'Delete draft' }))) return;
    setSaving(true);
    try {
      await service.deleteExperiment(report.upsellId, report.id);
      onMessage({ severity: 'success', text: `Draft "${report.label}" deleted.` });
      await onChanged();
    } catch (error) {
      onMessage({ severity: 'error', text: `Could not delete "${report.label}": ${errorText(error)}` });
    } finally {
      setSaving(false);
    }
  }

  const status = forced ? { label: `Showing ${forced.label} to everyone`, color: 'info' as const } : STATUS_CHIP[report.status];
  return (
    <Paper variant="outlined" sx={{ p: 2 }} aria-label={`${report.label} A/B test`}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>{report.label}</Typography>
        <Chip size="small" label={status.label} color={status.color} />
        <Typography variant="caption" color="text.secondary">
          {report.upsellId}
          {report.startedAt ? ` · started ${pacific(report.startedAt)}` : ` · created ${pacific(report.createdAt)}`}
          {report.stoppedAt ? ` · stopped ${pacific(report.stoppedAt)}` : ''}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Judged on profit per viewed offer. Each checkout sees one version; staff never enter a test. Plan: {planned} views
        per version, then pick one only if it is at least 90% likely the most profitable. Don't stop on an early lead: at
        this volume early leads are often noise.
      </Typography>
      {report.error && <Alert severity="error" sx={{ mb: 1.5 }}>Could not read its results ({report.error}).</Alert>}
      {report.problem && <Alert severity="warning" sx={{ mb: 1.5 }}>{report.problem}</Alert>}
      <VersionsChart arms={arms} />
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" aria-label={`${report.label} versions`}>
          <TableHead>
            <TableRow>
              <TableCell>Version</TableCell>
              <TableCell align="right">Charge</TableCell>
              <TableCell align="right">
                <Tooltip title="Profit per acceptance: the estimated margin at the charge, after the extra product cost, the card fee and freight; before refunds.">
                  <span>Profit / yes</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">Views</TableCell>
              <TableCell align="right">Yes</TableCell>
              <TableCell align="right">Take rate</TableCell>
              <TableCell align="right">
                <Tooltip title="Profit per viewed offer, with a 90% range: what the test is judged on.">
                  <span>Profit / view</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="How likely this version is the most profitable per view. Call it at 90%, at the planned views.">
                  <span>Chance best</span>
                </Tooltip>
              </TableCell>
              <TableCell>Split %</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {arms.map(arm => (
              <TableRow key={arm.designId} selected={shown.includes(arm.designId)}>
                <TableCell sx={{ minWidth: 180 }}>
                  <Button
                    size="small"
                    aria-pressed={shown.includes(arm.designId)}
                    onClick={() => setShown(current => (current.includes(arm.designId) ? current.filter(id => id !== arm.designId) : [...current, arm.designId]))}
                    sx={{ p: 0, minWidth: 0, textAlign: 'left', textTransform: 'none', color: 'inherit', font: 'inherit' }}
                  >
                    {arm.label}
                  </Button>
                  {arm.runnable === false && <Typography variant="caption" color="error" component="div">Can't be shown today</Typography>}
                  {planned > 0 && (
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, ((arm.viewed ?? 0) / planned) * 100)}
                      aria-label={`${arm.label}: ${arm.viewed ?? 0} of ${planned} planned views`}
                      sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                    />
                  )}
                </TableCell>
                <TableCell align="right">{arm.chargeCents == null ? '—' : usdCents(arm.chargeCents)}</TableCell>
                <TableCell align="right">{arm.profitPerAcceptCents == null ? '—' : usdCents(arm.profitPerAcceptCents)}</TableCell>
                <TableCell align="right">{arm.viewed ?? 0}</TableCell>
                <TableCell align="right">{arm.accepted ?? 0}</TableCell>
                <TableCell align="right">{pct(arm.accepted ?? 0, arm.viewed ?? 0)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {arm.profitPerViewCents
                    ? `${usdCents(Math.round(arm.profitPerViewCents.mid))} (${usdCents(Math.round(arm.profitPerViewCents.low))}–${usdCents(Math.round(arm.profitPerViewCents.high))})`
                    : '—'}
                </TableCell>
                <TableCell align="right">{arm.chanceBest == null ? '—' : `${Math.round(arm.chanceBest * 100)}%`}</TableCell>
                <TableCell>
                  {draft ? (
                    <TextField
                      size="small"
                      type="number"
                      value={split[arm.designId] ?? String(report.weights?.[arm.designId] ?? 0)}
                      disabled={saving}
                      onChange={event => setSplit(current => ({ ...current, [arm.designId]: event.target.value }))}
                      inputProps={{ min: 0, max: 100, 'aria-label': `${arm.label} percent of checkouts` }}
                      sx={{ width: 70 }}
                    />
                  ) : (
                    `${report.weights?.[arm.designId] ?? 0}%`
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">Click a version to see its page; click more to compare them.</Typography>
        <Button size="small" onClick={() => setShown(current => (current.length === arms.length ? [] : arms.map(arm => arm.designId)))}>
          {shown.length === arms.length ? 'Hide pages' : 'Compare side by side'}
        </Button>
      </Stack>
      {shown.length > 0 && (
        <CompareVersions ids={arms.map(arm => arm.designId).filter(id => shown.includes(id))} versions={versions} labels={labels} />
      )}
      {!draft && (
        <Typography variant="body2" sx={{ mt: 1.5 }}>
          {report.problem
            ? 'No odds while profit per acceptance is unknown (above).'
            : arms.some(arm => (arm.viewed ?? 0) < minViews)
              ? `Odds appear once every version has ${minViews} views; the call comes at ${planned}.`
              : !enough
                ? running
                  ? 'Too early to call: keep it running until every version reaches its planned views.'
                  : 'Too early to call: it stopped before every version reached its planned views.'
                : (leader?.chanceBest ?? 0) >= 0.9
                  ? `${leader.label} is ${Math.round((leader.chanceBest ?? 0) * 100)}% likely the most profitable per view: it can be made the winner.`
                  : 'No version is 90% likely the most profitable: the difference is too small to call at this volume.'}
        </Typography>
      )}
      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {draft && (
          <>
            <Button
              variant="contained"
              size="small"
              disabled={saving || !splitOk}
              onClick={async () => {
                const ok = await confirm({
                  title: `Start "${report.label}"?`,
                  body: (
                    <>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Qualifying dosaze.com customers will see one of these pages, split:
                      </Typography>
                      {arms.map(arm => (
                        <Typography key={arm.designId} variant="body2">
                          {arm.label}: <strong>{weights[arm.designId]}%</strong>
                        </Typography>
                      ))}
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        The split can't change once it runs. Checkouts follow within 30 seconds.
                      </Typography>
                    </>
                  ),
                  confirmLabel: 'Start test',
                  tone: 'warning',
                });
                if (ok) change({ status: 'running', weights }, `"${report.label}" started.`);
              }}
            >
              Start test
            </Button>
            {!splitOk && <Typography variant="caption" color="error">The split must be whole percents adding up to 100.</Typography>}
            <Button size="small" color="error" disabled={saving} onClick={remove}>Delete draft</Button>
          </>
        )}
        {running && (
          <Button
            size="small"
            variant="outlined"
            disabled={saving}
            onClick={async () => {
              const ok = await confirm({
                title: `Stop "${report.label}"?`,
                body: 'Everyone sees the upsell as written again. Its numbers are kept; to run it again, create a new test.',
                confirmLabel: 'Stop test',
              });
              if (ok) change({ status: 'stopped' }, `"${report.label}" stopped.`);
            }}
          >
            Stop test
          </Button>
        )}
        {running && !forced &&
          arms.map(arm => (
            <Button
              key={arm.designId}
              size="small"
              disabled={saving}
              onClick={async () => {
                const ok = await confirm({
                  title: `Show "${arm.label}" to everyone?`,
                  body: 'Every qualifying customer sees this version; the split pauses. Use it for the winner until it is written into upsells.js.',
                  confirmLabel: 'Show to everyone',
                  tone: 'warning',
                });
                if (ok) change({ forceVersion: arm.designId }, `Showing "${arm.label}" to everyone.`);
              }}
            >
              Show {arm.label.slice(0, 30)} to everyone
            </Button>
          ))}
        {forced && (
          <Button
            size="small"
            disabled={saving}
            onClick={async () => {
              const ok = await confirm({
                title: 'Go back to the split?',
                body: `Checkouts are split between the versions again instead of all seeing "${forced.label}".`,
                confirmLabel: 'Back to the split',
                tone: 'warning',
              });
              if (ok) change({ forceVersion: null }, 'Back to the split.');
            }}
          >
            Back to the split
          </Button>
        )}
        {report.status === 'stopped' && (
          <Typography variant="caption" color="text.secondary">Stopped. To compare again, create a new test from these versions.</Typography>
        )}
      </Box>
    </Paper>
  );
}

// Running first, then drafts, then stopped; newest first within each.
const ORDER: Record<ExperimentStatus, number> = { running: 0, draft: 1, stopped: 2 };

export function ExperimentsTab({ service, upsells, confirm, onChanged, onMessage }: {
  service: PostPurchaseService;
  upsells: Upsell[];
  confirm: Confirm;
  onChanged: () => Promise<void>;
  onMessage: (message: Message) => void;
}) {
  const experiments = upsells
    .flatMap(upsell => (upsell.experiments ?? []).map(experiment => ({ ...experiment, upsellId: upsell.id })))
    .sort((a, b) => ORDER[a.status] - ORDER[b.status] || String(b.createdAt).localeCompare(String(a.createdAt)));
  const [selected, setSelected] = React.useState<string | 'new' | null>(null);
  // Each upsell's versions, loaded once when first needed.
  const [versions, setVersions] = React.useState<Record<string, Versions>>({});
  const requested = React.useRef(new Set<string>());
  const loadVersions = React.useCallback(
    (upsellId: string) => {
      if (requested.current.has(upsellId)) return;
      requested.current.add(upsellId);
      service.getVersions(upsellId).then(
        found => setVersions(latest => ({ ...latest, [upsellId]: found })),
        () => setVersions(latest => ({ ...latest, [upsellId]: null })),
      );
    },
    [service],
  );
  const current = selected === 'new' || (!experiments.length && selected === null) ? null : experiments.find(experiment => experiment.id === selected) ?? experiments[0];
  React.useEffect(() => {
    if (current) loadVersions(current.upsellId);
  }, [current, loadVersions]);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'flex-start' }}>
      <Paper variant="outlined" sx={{ flex: '0 1 230px', minWidth: 200 }} aria-label="A/B tests">
        <Box sx={{ p: 1.5 }}>
          <Button fullWidth variant={current ? 'outlined' : 'contained'} size="small" onClick={() => setSelected('new')}>
            New experiment
          </Button>
        </Box>
        <Typography variant="overline" color="text.secondary" sx={{ px: 2, display: 'block' }}>Experiments</Typography>
        {!experiments.length && (
          <Typography variant="caption" color="text.secondary" sx={{ px: 2, pb: 1.5, display: 'block' }}>None yet.</Typography>
        )}
        {experiments.map(experiment => {
          const active = current?.id === experiment.id;
          const chip = experiment.forceVersion ? { label: 'One for everyone', color: 'info' as const } : STATUS_CHIP[experiment.status];
          return (
            <Box
              key={experiment.id}
              component="button"
              type="button"
              onClick={() => setSelected(experiment.id)}
              aria-current={active ? 'true' : undefined}
              sx={{
                display: 'block', width: '100%', textAlign: 'left', border: 0, cursor: 'pointer', px: 2, py: 1.25,
                bgcolor: active ? 'action.selected' : 'transparent', borderLeft: 3,
                borderLeftColor: active ? 'primary.main' : 'transparent', '&:hover': { bgcolor: 'action.hover' },
                font: 'inherit', color: 'inherit',
              }}
            >
              <Typography variant="body2" fontWeight={600}>{experiment.label}</Typography>
              <Typography variant="caption" color="text.secondary" component="div">
                {experiment.upsellId} · {experiment.versions.length} versions
              </Typography>
              <Chip size="small" label={chip.label} color={chip.color} sx={{ mt: 0.5 }} />
            </Box>
          );
        })}
      </Paper>
      <Box sx={{ flex: '1 1 640px', minWidth: 0 }}>
        {current ? (
          <ExperimentPanel
            key={current.id}
            report={current}
            versions={versions[current.upsellId]}
            service={service}
            confirm={confirm}
            onChanged={onChanged}
            onMessage={onMessage}
          />
        ) : (
          <NewExperiment
            upsells={upsells}
            versionsOf={upsellId => versions[upsellId]}
            loadVersions={loadVersions}
            service={service}
            onCreated={async (_, experimentId) => {
              setSelected(experimentId);
              await onChanged();
            }}
            onMessage={onMessage}
          />
        )}
      </Box>
    </Box>
  );
}
